import { useMemo, useCallback, useState } from "react"
import PropTypes from "prop-types"
import pluralize from "pluralize"
import { map, capitalize, pick, compact, size, filter, toUpper } from "lodash"
import { Paper } from "@mui/material"
import Table from "components/material/table"
import { ACCOUNT_MEMBER, ROLE } from "helpers/shapes"
import { formatLongDate } from "helpers/formatters/dates"
import { upsellingTitle } from "helpers/op-pro"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"
import EzeIntegrationDialog from "./components/eze-integration-dialog"
import FeaturePermissionsDialog, { updatePermissions, FEATURE } from "components/feature-permissions"

export default function Members({
  account,
  addNotification,
  hasEzeIntegration,
  isOpPro,
  isSuperuser,
  accountFeatures,
  memberFeatures,
  isMemberFeaturesLoading,
  members,
  openMemberFeatures,
  overProMemberLimitMessage,
  features,
  resendInvite,
  role,
  tabs,
  update,
  userId,
}) {
  const isAdmin = role === "admin"
  const nonEditable = (isOpPro && !isSuperuser)
  const disabledText = overProMemberLimitMessage ?? upsellingTitle(isOpPro, "member")

  const [user, setUser] = useState(null)
  const [dialog, setDialog] = useState(null)

  function handleShowDialog(dialog) {
    return (user) => {
      setUser(user)
      setDialog(dialog)
    }
  }

  const hideDialog = () => {
    setDialog(null)
    setUser(null)
  }

  const handleEzeIntegrationSubmit = (data, cb) => {
    update(`/api/${account}/members/${user.id}/eze`, { ezeUserId: data.ezeUserId }, account, {
      verb: "PUT",
      reloadData: "members",
    }, (errorMessage, result) => {
      if (!errorMessage) {
        setUser(null)
        setDialog(null)
      }
      cb(errorMessage, result)
    })
  }

  async function handleFeaturePermissionsSubmit(data) {
    const onError = (error) => addNotification("error", null, error)
    const onSuccess = (results) => {
      addNotification("success", null, `Updated ${size(results)} ${pluralize("feature", size(results))} permissions. (${user.name})`)
      setDialog(null)
    }
    await updatePermissions(data, features, memberFeatures, account, user, "users", update, onError, onSuccess)
  }

  const addForm = useMemo(() => {
    if (!isAdmin || nonEditable || overProMemberLimitMessage) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($name: String!, $email: Email!, $role: Role!) {
        addMember(member: { name: $name, email: $email, role: $role }) { id }
      }`

      if (isOpPro) {
        // Hide role field from pro, default to ADMIN
        data.role = "ADMIN"
      }

      update(query, data, account, {
        reloadData: "members",
      }, cb)
    }

    const addFormConfig = {
      title: "Add Member",
      submitText: "Add Member",
      track: {
        type: "new_member_added",
        payload: data => pick(data, "email", "role"),
      },
      onSubmit: onAdd,
      fields: [{
        id: "name",
        label: "Name",
        type: "string",
        validations: {
          isRequired: true,
          minLength: 4,
          maxLength: 50,
        },
      }, {
        id: "email",
        label: "Email",
        type: "email",
        validations: {
          isRequired: true,
        },
      }, {
        id: "role",
        label: "Role",
        type: "select",
        options: ["MEMBER", "ADMIN"],
        optionLabels: {
          MEMBER: "Account Member",
          ADMIN: "Account Admin",
        },
        validations: {
          isRequired: true,
        },
      }],
    }

    // Hide role field from pro, default to ADMIN
    if (isOpPro) {
      addFormConfig.fields = filter(
        addFormConfig.fields,
        f => f.id !== "role",
      )
    }

    return addFormConfig
  }, [isOpPro, isAdmin, nonEditable, update, account, overProMemberLimitMessage])
  const editForm = useMemo(() => {
    if (nonEditable) { return null }

    function onEdit(data, cb) {
      const query = `mutation ($id: IdOrEmail!, $role: Role!) {
        updateMemberRole(id: $id, role: $role) { id }
      }`

      update(query, data, account, {
        reloadData: "members",
      }, cb)
    }

    return {
      title: "Edit Member Role",
      isEnabled: () => isAdmin,
      submitText: "Save",
      track: {
        type: "edit_member_role",
        payload: data => pick(data, "role"),
      },
      onSubmit: onEdit,
      getInitialValues: ({ role }) => ({ role: toUpper(role) }),
      fields:
        [{
          id: "role",
          label: "Role",
          type: "select",
          options: ["MEMBER", "ADMIN"],
          optionLabels: {
            MEMBER: "Account Member",
            ADMIN: "Account Admin",
          },
          validations: {
            isRequired: true,
          },
        }],
    }
  }, [nonEditable, update, account, isAdmin])

  const deleteForm = useMemo(() => {
    if (nonEditable) { return null }

    function onDelete(data, cb) {
      const query = `mutation {
        ${map(data, (member, index) => `member${index}: removeMember(id: "${member.id}") { ok }`)}
      }`

      update(query, null, account, {
        reloadData: "members",
      }, cb)
    }

    return {
      type: "member",
      track: {
        type: "delete_member_confirmed",
        payload: (data) => pick(data, "email"),
      },
      isEnabled: () => isAdmin,
      renderItem: member => <li key={member.id}>{member.name} <span>({member.email})</span></li>,
      onSubmit: onDelete,
    }
  }, [nonEditable, update, account, isAdmin])

  const linkTo = useCallback((member) => {
    if (role === "admin" || member.id === userId) {
      return `/${account}/home/members/${member.id}`
    }
  }, [account, role, userId])

  // OpPro: Remove Member page link and Role column
  const columns = compact([{
    name: "Name",
    id: "name",
    ...(!isOpPro && { linkTo }),
  }, {
    name: "Email",
    id: "email",
  }, {
    name: "Created",
    id: "createdAt",
    formatter: formatLongDate,
  }, !isOpPro && {
    name: "Role",
    id: "role",
    formatter: role => `Account ${capitalize(role)}`,
  }, isSuperuser && hasEzeIntegration && {
    name: "Eze User ID",
    id: "ezeUserId",
  }])

  const additionalOptions = compact([
    {
      id: "resend",
      label: "Resend Invitation",
      displayFor: member => role === "admin" && !member.isActivated,
      onClick: member => resendInvite(member.id),
    },
    isSuperuser && hasEzeIntegration && {
      id: "ezeIntegration",
      label: "Eze Integration...",
      displayFor: () => isSuperuser,
      onClick: handleShowDialog("eze"),
    },
    isSuperuser && features && {
      id: "manage-features",
      label: "Manage features access",
      onClick: (member) => {
        openMemberFeatures(member.id)
        handleShowDialog("features")(member)
      },
    }])

  return <div className="module_members">
    <UpsellTabs id="members" tabs={tabs} />
    <Paper className="content" square>
      <Table
        stickyHeaders
        title="Members"
        idField="id"
        addForm={addForm}
        addDisabledText={disabledText}
        editForm={editForm}
        deleteForm={deleteForm}
        data={members}
        columns={columns}
        additionalOptions={additionalOptions}
      />
    </Paper>
    <EzeIntegrationDialog show={dialog === "eze"} onSubmit={handleEzeIntegrationSubmit} onClose={hideDialog} user={user} />
    <FeaturePermissionsDialog show={dialog === "features"} onSubmit={handleFeaturePermissionsSubmit} onClose={hideDialog} entity={user} features={features} accountFeatures={accountFeatures} entityFeatures={memberFeatures} entityLabel="member" isLoading={isMemberFeaturesLoading} />
  </div>
}

Members.propTypes = {
  account: PropTypes.string.isRequired,
  addNotification: PropTypes.func.isRequired,
  hasEzeIntegration: PropTypes.bool,
  isOpPro: PropTypes.bool.isRequired,
  isSuperuser: PropTypes.bool.isRequired,
  memberFeatures: PropTypes.arrayOf(FEATURE),
  accountFeatures: PropTypes.arrayOf(FEATURE),
  members: PropTypes.arrayOf(ACCOUNT_MEMBER),
  openMemberFeatures: PropTypes.func.isRequired,
  overProMemberLimitMessage: PropTypes.string,
  features: PropTypes.arrayOf(FEATURE),
  resendInvite: PropTypes.func.isRequired,
  role: ROLE.isRequired,
  tabs: UPSELL_TABS_SHAPE,
  update: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  isMemberFeaturesLoading: PropTypes.bool,
}
